import type { ReactNode } from 'react'

import { Badge } from '@/components/ui/badge'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { PipelinePopoverPanel, usePipelineHoverPopover } from '@/components/pipeline/pipeline-popover-panel'
import { useJobEventsBreakdown } from '@/stores/pipeline.store'
import { formatShortDate } from '@/lib/date-utils'
import { formatEnumName, getEventStageColor } from '@/lib/enum-utils'
import type { JobLite } from '@/gen/cortado/v1/jobs_pb'
import { EventStageEnum } from '@/gen/cortado/v1/settings_pb'
import { pipelineEventDateRange, pipelineEventsMeta } from '@/components/pipeline/pipeline-job-view'

interface PipelineEventsPopoverCellProps {
  job: JobLite
  /** Replaces the default date-range + meta trigger (the board card shows only the meta line). */
  children?: ReactNode
  /** Replaces the default trigger button classes. */
  triggerClassName?: string
}

/**
 * Events cell that lazily fetches the job's events via `ListJobEvents` only
 * once its popover is opened. Opens on hover (with a short open/close delay
 * — see `usePipelineHoverPopover`) or click/focus so it stays keyboard
 * accessible — mirrors `PipelineInvoicePopoverCell`.
 */
export function PipelineEventsPopoverCell({ job, children, triggerClassName }: PipelineEventsPopoverCellProps) {
  const { open, onOpenChange, triggerHandlers, contentHandlers } = usePipelineHoverPopover()
  const { events, isLoading, isError, permissionDenied } = useJobEventsBreakdown(job.id, open)

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={triggerClassName ?? 'block w-full min-w-0 rounded-md px-1 py-0.5 text-left hover:bg-accent'}
          onClick={(event) => event.stopPropagation()}
          {...triggerHandlers}
        >
          {children ?? (
            <>
              <div className="font-financial truncate">{pipelineEventDateRange(job)}</div>
              <div className="truncate text-xs text-muted-foreground">{pipelineEventsMeta(job)}</div>
            </>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={0}
        className="w-96 p-0 text-left"
        onClick={(event) => event.stopPropagation()}
        {...contentHandlers}
      >
        <div className="text-xs">
          <PipelinePopoverPanel
            title="Events on this job"
            count={events.length}
            isLoading={isLoading}
            loadingText="Loading events…"
            isError={isError}
            permissionDenied={permissionDenied}
            permissionDeniedText="You don't have permission to view events."
            errorText="Couldn't load events."
            isEmpty={events.length === 0}
            emptyText="No events yet."
          >
            {events.map((event) => {
              const archived = !!event.archivedAt
              return (
                <div
                  key={event.id}
                  className={`grid grid-cols-[1fr_auto] items-center gap-2.5 border-b border-border px-3 py-2 last:border-b-0 ${archived ? 'opacity-50' : ''}`}
                >
                  <div className="min-w-0">
                    <div className="font-medium text-foreground">
                      {event.eventTypeLabel || event.eventTypeKey || 'Event'}
                    </div>
                    <div className="font-financial text-muted-foreground">
                      {event.eventDate ? formatShortDate(event.eventDate, event.eventTimezone) : 'No date set'}
                      {event.eventGuests != null ? ` · ${event.eventGuests} guests` : ''}
                    </div>
                    {event.eventLocation ? (
                      <div className="truncate text-muted-foreground">{event.eventLocation}</div>
                    ) : null}
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    {event.stage != null ? (
                      <Badge variant={getEventStageColor(event.stage)}>
                        {archived ? 'Archived' : eventStageLabel(event.stage)}
                      </Badge>
                    ) : archived ? (
                      <Badge variant="secondary">Archived</Badge>
                    ) : null}
                  </div>
                </div>
              )
            })}
          </PipelinePopoverPanel>
        </div>
      </PopoverContent>
    </Popover>
  )
}

function eventStageLabel(stage: EventStageEnum): string {
  return formatEnumName(stage, EventStageEnum)
}
